/**
 * Where the token pair lives between requests.
 */

export interface StoredTokens {
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
}

export interface TokenStore {
  read(): StoredTokens | null;
  write(tokens: StoredTokens): void;
  clear(): void;
  /** Notified on every change, so an open tab can react to a sign-out in another one. */
  subscribe(listener: (tokens: StoredTokens | null) => void): () => void;
}

const STORAGE_KEY = "breenova.tokens";

/**
 * Persists the pair in `localStorage`.
 *
 * The honest trade-off: `localStorage` is readable by any script running on the page, so
 * an XSS bug hands over both tokens. An httpOnly cookie would not be — but BreeNova also
 * ships as a Capacitor app served from `capacitor://localhost`, where the cookie a
 * browser would set for the API's origin is not reliably sent, and a breeder standing in
 * an aviary being silently signed out is a bug they will actually hit. So: one storage
 * mechanism that works in both shells, and the defence against theft is rotation plus
 * reuse detection on the server rather than making the token unreadable here.
 *
 * Every access is guarded — Safari private mode throws on `localStorage` rather than
 * returning null, and an auth layer that throws on read locks the breeder out entirely.
 */
export function createLocalStorageTokenStore(): TokenStore {
  const listeners = new Set<(tokens: StoredTokens | null) => void>();

  // Mirrored in memory so a read never depends on storage being available.
  let cached: StoredTokens | null = read();

  function read(): StoredTokens | null {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredTokens) : null;
    } catch {
      return null;
    }
  }

  function notify(tokens: StoredTokens | null): void {
    for (const listener of listeners) {
      listener(tokens);
    }
  }

  // A sign-out in another tab should not leave this one thinking it is still signed in.
  if (typeof globalThis.addEventListener === "function") {
    globalThis.addEventListener("storage", (event) => {
      if ((event as StorageEvent).key !== STORAGE_KEY) {
        return;
      }

      cached = read();
      notify(cached);
    });
  }

  return {
    read: () => cached,

    write(tokens) {
      cached = tokens;
      try {
        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(tokens));
      } catch {
        // Storage unavailable. The in-memory copy still carries this session; the
        // breeder just has to sign in again after a reload.
      }
      notify(tokens);
    },

    clear() {
      cached = null;
      try {
        globalThis.localStorage?.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to do — the in-memory copy is already gone, which is what matters.
      }
      notify(null);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** For tests and for server-side rendering, where there is no storage at all. */
export function createMemoryTokenStore(): TokenStore {
  let tokens: StoredTokens | null = null;
  const listeners = new Set<(tokens: StoredTokens | null) => void>();

  const notify = (): void => listeners.forEach((listener) => listener(tokens));

  return {
    read: () => tokens,
    write(next) {
      tokens = next;
      notify();
    },
    clear() {
      tokens = null;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
